// ==================== SUB-ACCOUNTS SERVICE ====================
// Sub-account logins under a customer's organization.
// The customer manages its own (/customer/sub-accounts); the super admin manages
// any customer's (/admin/customers/:id/sub-accounts). Both use the same calls.
// Only the customer assigns contacts to its sub-accounts.

import { apiService } from './api';
import type { PaginatedResponse } from '../types';

// ==================== TYPES ====================

export interface SubAccount {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  /** Team member's contact details (sub-accounts created before these fields existed have none) */
  personalEmail: string | null;
  phoneNumber: string | null;
  /** Contacts the customer assigned to this sub-account */
  contactCount: number;
  /** The account (organization) the sub-account works in; the main one unless the customer chose another */
  assignedOrganizationId: string;
}

export interface CreateSubAccountData {
  /** Multiple organizations: the account the sub-account works in (default: main) */
  organizationId?: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  personalEmail: string;
  phoneNumber: string;
}

export interface UpdateSubAccountData {
  /** Multiple organizations: the account the sub-account works in */
  organizationId?: string;
  firstName?: string;
  lastName?: string;
  personalEmail?: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface SubAccountContact {
  id: string;
  name?: string;
  phoneNumber: string;
  email?: string;
  country?: string;
  optedIn: boolean;
  createdAt: string;
  tags: string[];
}

export interface SubAccountContactsParams {
  search?: string;
  optedIn?: boolean;
  page?: number;
  limit?: number;
}

/** The sub-account calls for one organization (the customer's own, or one customer's for the admin) */
export interface SubAccountsApi {
  getAll: () => Promise<SubAccount[]>;
  create: (data: CreateSubAccountData) => Promise<SubAccount>;
  update: (id: string, data: UpdateSubAccountData) => Promise<SubAccount>;
  resetPassword: (id: string, password: string) => Promise<void>;
  delete: (id: string) => Promise<void>;
}

// ==================== SERVICE ====================

const createSubAccountsApi = (basePath: string): SubAccountsApi => ({
  async getAll(): Promise<SubAccount[]> {
    const response = await apiService.get<SubAccount[]>(basePath);
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch sub-accounts');
    }
    return response.data || [];
  },

  async create(data: CreateSubAccountData): Promise<SubAccount> {
    const response = await apiService.post<SubAccount>(basePath, data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to create sub-account');
    }
    return response.data as SubAccount;
  },

  async update(id: string, data: UpdateSubAccountData): Promise<SubAccount> {
    const response = await apiService.patch<SubAccount>(`${basePath}/${id}`, data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to update sub-account');
    }
    return response.data as SubAccount;
  },

  async resetPassword(id: string, password: string): Promise<void> {
    const response = await apiService.post(`${basePath}/${id}/reset-password`, { password });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update sub-account password');
    }
  },

  async delete(id: string): Promise<void> {
    const response = await apiService.delete(`${basePath}/${id}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete sub-account');
    }
  },
});

const buildContactsQuery = (params?: SubAccountContactsParams): string => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.optedIn !== undefined) queryParams.append('optedIn', String(params.optedIn));
  if (params?.page) queryParams.append('page', String(params.page));
  if (params?.limit) queryParams.append('limit', String(params.limit));
  return queryParams.toString() ? `?${queryParams}` : '';
};

const CUSTOMER_BASE_PATH = '/customer/sub-accounts';

/** The logged-in customer's own sub-accounts, and the contacts assigned to them */
export const subAccountsService = {
  ...createSubAccountsApi(CUSTOMER_BASE_PATH),

  async getContacts(
    id: string,
    params?: SubAccountContactsParams,
  ): Promise<PaginatedResponse<SubAccountContact>> {
    const response = await apiService.get<PaginatedResponse<SubAccountContact>>(
      `${CUSTOMER_BASE_PATH}/${id}/contacts${buildContactsQuery(params)}`,
    );
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch sub-account contacts');
    }
    return response.data;
  },

  async getAvailableContacts(
    id: string,
    params?: Omit<SubAccountContactsParams, 'optedIn'>,
  ): Promise<PaginatedResponse<SubAccountContact>> {
    const response = await apiService.get<PaginatedResponse<SubAccountContact>>(
      `${CUSTOMER_BASE_PATH}/${id}/available-contacts${buildContactsQuery(params)}`,
    );
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch available contacts');
    }
    return response.data;
  },

  async addContacts(id: string, contactIds: string[]): Promise<{ added: number }> {
    const response = await apiService.post<{ added: number }>(
      `${CUSTOMER_BASE_PATH}/${id}/contacts/add`,
      { contactIds },
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to assign contacts');
    }
    return response.data || { added: 0 };
  },

  async removeContacts(id: string, contactIds: string[]): Promise<{ removed: number }> {
    const response = await apiService.post<{ removed: number }>(
      `${CUSTOMER_BASE_PATH}/${id}/contacts/remove`,
      { contactIds },
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to remove contacts');
    }
    return response.data || { removed: 0 };
  },
};

/** A customer's sub-accounts, managed by the super admin */
export const adminSubAccountsService = {
  forCustomer: (customerId: string): SubAccountsApi =>
    createSubAccountsApi(`/admin/customers/${customerId}/sub-accounts`),
};

export default subAccountsService;
